import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AlertBox, Button, SvgIcon } from '@/components';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { amlTraceActions } from '@/redux/actions';
import { amlTraceSelectors, userSelectors } from '@/redux/selectors';
import { AmlTraceRequest, AmlTraceData } from '@/api';
import { showEmailConfirmationToast } from '@/helpers';
import { navigationPages } from '@/config';
import TraceEntryForm from '../TraceEntryForm/TraceEntryForm';
import TraceEntryChip from '../TraceEntryChip/TraceEntryChip';
import { TraceEntry } from './TraceInvestigationForm.types';

import styles from './TraceInvestigationForm.module.sass';

interface TraceInvestigationFormProps {
  isDisabled: boolean;
}

const TraceInvestigationForm = ({
  isDisabled,
}: TraceInvestigationFormProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { loading, error } = useAppSelector(amlTraceSelectors.getTraceSlice);
  const activeUuid = useAppSelector(amlTraceSelectors.getActiveUuid);
  const isEmailConfirmed = useAppSelector(userSelectors.checkIfEmailConfirmed);

  const [entries, setEntries] = useState<TraceEntry[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(true);
  const pendingSubmitRef = useRef(false);

  const handleConfirmEntry = useCallback(
    (entry: TraceEntry) => {
      setEntries((prev) => {
        if (editingId) {
          return prev.map((e) => (e.id === editingId ? entry : e));
        }
        return [...prev, entry];
      });
      setEditingId(null);
      setIsAdding(false);
    },
    [editingId],
  );

  const handleRemoveEntry = useCallback((id: string) => {
    setEntries((prev) => {
      const next = prev.filter((e) => e.id !== id);
      if (next.length === 0) {
        setIsAdding(true);
      }
      return next;
    });
  }, []);

  const handleEditEntry = useCallback((id: string) => {
    setEditingId(id);
    setIsAdding(true);
  }, []);

  const handleCancelEntry = useCallback(() => {
    if (editingId) {
      setEntries((prev) => {
        const next = prev.filter((e) => e.id !== editingId);
        setIsAdding(next.length === 0);
        return next;
      });
    } else {
      setIsAdding(false);
    }
    setEditingId(null);
  }, [editingId]);

  const editingEntry = entries.find((e) => e.id === editingId) || null;

  const submitDisabled =
    isDisabled || loading || entries.length === 0 || isAdding;

  const submitHandler = useCallback(() => {
    if (!isEmailConfirmed) {
      showEmailConfirmationToast();
      return;
    }

    if (loading || entries.length === 0) return;

    dispatch(amlTraceActions.setError(null));
    pendingSubmitRef.current = true;

    const traces: AmlTraceData[] = entries.map((entry) => ({
      blockchain: entry.chain,
      asset: entry.displayAsset,
      hash: entry.hash,
      tokenId: entry.tokenId,
    }));

    const payload: AmlTraceRequest = { traces };
    dispatch(amlTraceActions.startTrace(payload));
  }, [dispatch, entries, isEmailConfirmed, loading]);

  useEffect(() => {
    if (!loading && pendingSubmitRef.current && !error && activeUuid) {
      pendingSubmitRef.current = false;
      navigate(navigationPages.TRACE_INVESTIGATION_DETAILS.path, {
        state: {
          uuid: activeUuid,
        },
      });
    }
  }, [loading, error, activeUuid, navigate]);

  return (
    <div className={styles.root}>
      {entries
        .filter((entry) => entry.id !== editingId)
        .map((entry) => (
          <TraceEntryChip
            key={entry.id}
            entry={entry}
            onEdit={() => handleEditEntry(entry.id)}
            onRemove={() => handleRemoveEntry(entry.id)}
          />
        ))}

      {isAdding && (
        <TraceEntryForm
          key={editingId || 'draft'}
          isDisabled={isDisabled}
          onConfirm={handleConfirmEntry}
          onCancel={handleCancelEntry}
          canCancel={entries.length > 0}
          initialValue={editingEntry}
        />
      )}

      {!isAdding && (
        <button
          type="button"
          className={styles.addAnother}
          onClick={() => setIsAdding(true)}
        >
          <SvgIcon name="plus" />
          {t('translation.ai_investigation_add_another')}
        </button>
      )}

      {error && (
        <AlertBox type="danger" small>
          {{ description: error }}
        </AlertBox>
      )}

      <Button
        corner="rect"
        className={styles.submit}
        onClick={submitHandler}
        disabled={submitDisabled}
      >
        {t('translation.ai_investigation_cta_prefix')}
        <span>|</span>
        <span className={styles.btnIcon}>
          <SvgIcon name="aml" />
          10
        </span>
      </Button>
    </div>
  );
};

export default TraceInvestigationForm;
